// DashboardLayout.jsx
import React, { useState } from 'react';
import { Box, Toolbar, Typography, useMediaQuery, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import HeaderBar from '../components/HeaderBar';
import LeftDrawer from '../components/LeftDrawer';
import Breadcrumbs from '../components/common/Breadcrumbs';
import DashboardCards from '../components/dashboard/DashboardCards';
import RecentActivities from '../components/dashboard/RecentActivities';
import QuickActions from '../components/dashboard/QuickActions';
import FloatingActionButton from '../components/common/FloatingActionButton';
import { useToast } from '../components/common/ToastProvider';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { showSuccess } = useToast();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleFloatingAction = (action) => {
    showSuccess(`${action.replace('-', ' ')} feature coming soon!`);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/dashboard"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: '100vh',
          width: { sm: `calc(100% - ${isMobile ? 0 : 260}px)` }
        }}
      >
        <Toolbar sx={{ minHeight: 64 }} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.02em'
              }}
            >
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
          
          {/* Dashboard Cards */}
          <Box sx={{ mb: 4 }}>
            <DashboardCards />
          </Box>
          
          {/* Quick Actions */}
          <Box sx={{ mb: 4 }}>
            <QuickActions />
          </Box>
          
          {/* Recent Activities and Additional Content */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <RecentActivities />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Upcoming Tasks Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.04)'
                        : 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
                        : '0px 2px 8px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Upcoming Tasks
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main'
                        }} />
                        <Typography variant="body2" color="text.secondary">
                          Monthly interest calculation (Due: Tomorrow)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main'
                        }} />
                        <Typography variant="body2" color="text.secondary">
                          Loan approval review (Due: 2 days)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'success.main'
                        }} />
                        <Typography variant="body2" color="text.secondary">
                          Member verification (Due: 3 days)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>

                {/* System Status Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(76, 175, 80, 0.08)'
                        : 'rgba(76, 175, 80, 0.05)',
                      border: '1px solid',
                      borderColor: 'success.main',
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
                        : '0px 2px 8px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                      System Status
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'success.main'
                        }} />
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          All systems operational
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'success.main'
                        }} />
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          Database backup completed
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'success.main'
                        }} />
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          Security scan passed
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
        
        {/* Floating Action Button */}
        <FloatingActionButton onAction={handleFloatingAction} />
      </Box>
    </Box>
  );
};

export default Dashboard;
